import React, { useEffect, useState } from "react";
import { Shield, ExternalLink, Mail } from "lucide-react";
import { useLanguage } from "../components/LanguageContext";
import SEO from "../components/SEO";

export default function Sponsors() {
  const { lang, t } = useLanguage();
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const response = await fetch("https://api.dev.auxo.dk/public/v2/content/sponsors");
        if (!response.ok) {
          throw new Error(lang === "da" ? "Kunne ikke hente sponsorer" : "Could not fetch sponsors");
        }
        const data = await response.json();
        setSponsors(data.items || []);
      } catch (err) {
        console.error("Fejl ved hentning af sponsorer:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSponsors();
  }, [lang]);

  return (
    <div className="bg-[#FCF9F5] text-stone-900 py-24 font-sans-custom">
      <SEO pageKey="sponsors" />

      <div className="mx-auto max-w-7xl px-6 md:px-12">
        {/* Header */}
        <div className="max-w-3xl space-y-6 mb-20">
          <div className="inline-flex items-center gap-3 border border-stone-300 bg-stone-100 px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] text-stone-800 rounded-none font-medium font-sans-custom">
            <Shield className="h-3.5 w-3.5 text-stone-700" />
            <span>{t.sponsors.badge}</span>
          </div>
          <h1 className="font-display text-3xl sm:text-5xl md:text-6xl uppercase tracking-normal sm:tracking-wide-custom text-stone-900 font-medium">
            {t.sponsors.title}{" "}
            <span className="italic text-stone-600 lowercase font-display normal-case">
              {t.sponsors.title_italic}
            </span>
          </h1>
          <p className="text-sm text-stone-600 leading-relaxed font-light tracking-wide-custom font-sans-custom">
            {t.sponsors.desc}
          </p>
        </div>

        {/* Loading / Error States */}
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <div className="h-8 w-8 animate-spin border-2 border-stone-800 border-t-transparent rounded-none" />
          </div>
        ) : error ? (
          <div className="border border-red-200 bg-red-50/50 p-6 text-center text-red-600 rounded-none">
            {t.sponsors.error}: {error}.
          </div>
        ) : (
          /* Grid of Sponsors */
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 mb-20">
            {sponsors.map((sponsor) => {
              const isSamshield = sponsor.name?.toLowerCase().includes("samshield");
              return (
                <a
                  key={sponsor.id}
                  href={sponsor.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group flex flex-col items-center justify-center border border-stone-200 bg-white p-8 text-center transition-all duration-500 hover:border-stone-400 hover:bg-stone-50 rounded-none shadow-sm ${
                    isSamshield ? "sm:col-span-2 lg:col-span-1" : ""
                  }`}
                >
                  <div className="relative aspect-[3/2] w-full max-w-[200px] md:max-w-[280px] overflow-hidden bg-stone-50 p-4 flex items-center justify-center border border-stone-200 group-hover:border-stone-400 transition-colors duration-300 rounded-none">
                    <img
                      src={sponsor.logo?.[0]}
                      alt={sponsor.name}
                      className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <h2 className="mt-6 font-display text-lg text-stone-800 group-hover:text-stone-900 transition-colors duration-300 flex items-center gap-1.5 uppercase tracking-wide-custom font-medium">
                    <span>{sponsor.name}</span>
                    <ExternalLink className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </h2>
                  {sponsor.description && (
                    <p className="mt-2 text-xs text-stone-500 font-light leading-relaxed max-w-xs font-sans-custom">
                      {sponsor.description}
                    </p>
                  )}
                </a>
              );
            })}
          </div>
        )}

        {/* Ready for more sponsors section */}
        <div className="border border-dashed border-stone-300 bg-white p-8 md:p-12 text-center max-w-3xl mx-auto space-y-6 rounded-none shadow-sm">
          <h3 className="font-display text-xl sm:text-2xl text-stone-900 uppercase tracking-normal sm:tracking-wide-custom font-medium">{t.sponsors.partner_title}</h3>
          <p className="text-xs text-stone-500 leading-relaxed max-w-xl mx-auto font-light tracking-wide-custom font-sans-custom">
            {t.sponsors.partner_desc}
          </p>
          <div className="pt-2">
            <a
              href="mailto:info@equinesport.dk"
              className="inline-flex items-center gap-2 bg-stone-800 hover:bg-stone-900 text-stone-50 px-4 py-2.5 sm:px-6 sm:py-3.5 text-[10px] sm:text-xs font-semibold uppercase tracking-widest transition-colors duration-300 rounded-none cursor-pointer font-sans-custom"
            >
              <Mail className="h-4 w-4" />
              <span>{t.sponsors.partner_btn}</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
